import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';

// In-app replacement for window.confirm:
//   if (!(await confirmDialog({ title: 'Delete this user?', danger: true }))) return;
// Mounted on demand, so no provider is needed. Escape or a click outside cancels.
const Dialog = ({
  title,
  message,
  confirmLabel,
  cancelLabel,
  danger,
  onClose,
}) => {
  const confirmRef = useRef(null);

  useEffect(() => {
    confirmRef.current?.focus();
    const onKey = (e) => e.key === 'Escape' && onClose(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    // eslint-disable-next-line
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-70 p-4 backdrop-blur-sm"
      onClick={() => onClose(false)}
    >
      {/* eslint-disable-next-line */}
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm space-y-3 rounded-2xl bg-zinc-900 p-5 shadow-xl"
      >
        <h3
          id="confirm-dialog-title"
          className="text-base font-semibold text-white"
        >
          {title}
        </h3>
        {message && <p className="text-sm text-zinc-400">{message}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={() => onClose(false)}
            className="rounded-md border border-zinc-700 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-800"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            ref={confirmRef}
            onClick={() => onClose(true)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium text-white ${
              danger
                ? 'bg-red-600 hover:bg-red-500'
                : 'bg-indigo-600 hover:bg-indigo-500'
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export const confirmDialog = ({
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
}) =>
  new Promise((resolve) => {
    const host = document.createElement('div');
    document.body.appendChild(host);
    const root = ReactDOM.createRoot(host);
    const onClose = (answer) => {
      root.unmount();
      host.remove();
      resolve(answer);
    };
    root.render(
      <Dialog
        title={title}
        message={message}
        confirmLabel={confirmLabel}
        cancelLabel={cancelLabel}
        danger={danger}
        onClose={onClose}
      />,
    );
  });
